import React, { useEffect, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  FlatList,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { setItem } from "@lib/storage";
import Toast from "react-native-simple-toast";

import analytics from "@services/analytics";
import Text from "@components/CustomText";
import { StackActions } from "@react-navigation/native";
import { getDropDownOptions, updateBasicInfo } from "@services/api";
import { colors } from "@constants/theme";
import { IJ_EVENTS } from "@constants/analyticsEventsCT";
import { PAYMENT_PENDING_MODAL, SHOW_IJ_FLOW } from "@constants/strings";
import t from "@constants/localization";

import { actions as authActions } from "@reducers/authReducer";
import CustomDropDownBox from "./CustomDropDownBox";
import CustomTextInputBox from "./CustomTextInputBox";
import CustomWheelPicker from "./CustomWheelPicker";
import MessageWrapper from "./MessageWrapper";
import color from "@constants/color";
import Button from "@components/Button";
import ArrowRightMedium from "@assets/svg/ArrowRightMedium";

const PATH = "screens/CLFlow/components/MessageBox";

const ListHeaderComponent = () => {
  return (
    <MessageWrapper>
      <View style={styles.messageContainer}>
        <Text>{t(`${PATH}/completeApplication`)}</Text>
      </View>
    </MessageWrapper>
  );
};

const MessageBox = (props) => {
  const { route, navigation } = props;

  const { ...userDetails } = useSelector(({ auth: { user } }) => user);
  const user = useSelector((state) => state.auth.user);

  const [clDropDownOptions, setClDropDownOptions] = useState([
    ...(userDetails.clDropDownOptions || []),
  ]);
  const [loading, setLoading] = useState(true);
  const [messageData, setMessageData] = useState([]);
  const [updatedPayload, setUpdatedPayload] = useState({});
  const [checkIsLast, setCheckIsLast] = useState(false);

  const scrollRef = useRef();
  const dispatch = useDispatch();

  const getInitialData = () => {
    let initialData = {};
    clDropDownOptions?.forEach((el, i) => {
      initialData = Object.assign(initialData, {
        [el.name]: userDetails[el.name],
      });
    });
    return initialData;
  };

  useEffect(() => {
    fetchCLDropDownOptions();
  }, [userDetails.clDropDownOptions]);

  const fetchCLDropDownOptions = async () => {
    try {
      const { data } = await getDropDownOptions();
      const { clDropDownOptions = [] } = data || {};
      setClDropDownOptions(clDropDownOptions);
      if (
        Array.isArray(clDropDownOptions) &&
        clDropDownOptions[clDropDownOptions.length - 1]?.isLast &&
        user?.source
      )
        enbaleNextButton();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getItems();
    setLoading(false);
    setTimeout(() => {
      scrollRef?.current?.scrollToEnd();
    }, 500);
  }, [getItems, user, clDropDownOptions]);

  const onSkip = async (val, num) => {
    try {
      await setItem(SHOW_IJ_FLOW, "false");
      await setItem(
        PAYMENT_PENDING_MODAL,
        JSON.stringify(new Date().getDay() + 1),
      );
      analytics.trackEvent(IJ_EVENTS.IJ_SKIPPED, {
        source: "skip_btn",
        question: val,
      });
    } catch (error) {
      console.log(error);
    }
    dispatch(authActions.disableInitialJourneyWhenComplete());
  };

  const onComplete = async () => {
    try {
      await setItem(SHOW_IJ_FLOW, "false");
      analytics.trackEvent(IJ_EVENTS.IJ_COMPLETED);
      await setItem(
        PAYMENT_PENDING_MODAL,
        JSON.stringify(new Date().getDay() + 1),
      );
      Toast.show(t(`${PATH}/formSubmitted`), Toast.LONG);
      dispatch(authActions.disableInitialJourneyWhenComplete());
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnSubmit = async (isLast = false, payload = {}) => {
    const initialData = getInitialData();
    const finalPayload = { ...initialData, ...updatedPayload, ...payload };
    dispatch({
      type: "UPDATE_BASIC_INFO_IN_USER",
      payload: finalPayload,
    });
    // in case api fails reset to previous state
    const payloadCopy = updatedPayload;
    setUpdatedPayload({ ...updatedPayload, ...finalPayload });
    analytics.trackEvent(IJ_EVENTS.IJ_ANSWER_SUBMITTED, {
      ...payload,
      source: "IJ",
    });
    try {
      await updateBasicInfo(finalPayload);
      if (isLast) {
        enbaleNextButton();
      }
    } catch (error) {
      setUpdatedPayload(payloadCopy);
    }
  };

  const enbaleNextButton = () => {
    setCheckIsLast(true);
    setTimeout(() => {
      scrollRef?.current?.scrollToEnd();
    }, 500);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getItems = async () => {
    const initialData = getInitialData();
    const items = [];
    for (let i = 0; i < clDropDownOptions?.length; i++) {
      const method = "push";
      if (clDropDownOptions[i]?.show_ij) {
        if (
          clDropDownOptions[i]?.name === "leader_shop" &&
          initialData[clDropDownOptions[i - 1]?.name] !== "businessman"
        ) {
          continue;
        }
        if (!initialData[clDropDownOptions[i]?.name]) {
          switch (clDropDownOptions[i]?.component) {
            case "CustomTextInput":
              items[method](
                <CustomTextInputBox
                  item={clDropDownOptions[i]}
                  value={initialData[clDropDownOptions[i]?.name]}
                  onSubmit={handleOnSubmit}
                  onSkip={() => onSkip(clDropDownOptions[i]?.name)}
                />,
              );
              break;
            case "CustomDropDownInput":
              items[method](
                <CustomDropDownBox
                  item={clDropDownOptions[i]}
                  value={initialData[clDropDownOptions[i]?.name]}
                  onSelect={handleOnSubmit}
                />,
              );
              break;
            case "CustomWheelPicker":
              items[method](
                <CustomWheelPicker
                  item={clDropDownOptions[i]}
                  onSubmit={handleOnSubmit}
                  value={initialData[clDropDownOptions[i]?.name]}
                />,
              );
          }
          break;
        } else {
          switch (clDropDownOptions[i]?.component) {
            case "CustomTextInput":
              items[method](
                <CustomTextInputBox
                  item={clDropDownOptions[i]}
                  value={initialData[clDropDownOptions[i]?.name]}
                  onSubmit={handleOnSubmit}
                  onSkip={() => onSkip(clDropDownOptions[i]?.name)}
                />,
              );
              break;
            case "CustomDropDownInput":
              items[method](
                <CustomDropDownBox
                  item={clDropDownOptions[i]}
                  value={initialData[clDropDownOptions[i]?.name]}
                  onSelect={handleOnSubmit}
                />,
              );
              break;
            case "CustomWheelPicker":
              items[method](
                <CustomWheelPicker
                  item={clDropDownOptions[i]}
                  value={initialData[clDropDownOptions[i]?.name]}
                  onSubmit={handleOnSubmit}
                />,
              );
          }
        }
      }
    }
    setMessageData(items);
    return items;
  };

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }
  const renderListData = (item, index) => {
    const {
      isLast = false,
      oneClick = false,
      name = "",
      heading = "",
    } = item?.props?.item || {};
    return (
      <View key={`${index}${name}${heading}`}>
        {item}
        {isLast ? <RenderLastComponent /> : null}
      </View>
    );
  };

  const RenderLastComponent = () => {
    return (
      <View style={styles.footerContainer}>
        <Button
          buttonWidth="100%"
          btnText={t(`${PATH}/moveNext`)}
          buttonFontType="title_18_semi"
          height={48}
          borderRadius={10}
          onPress={onComplete}
          disabled={!checkIsLast}
          containerStyle={styles.buttonContainer}
          IconRight={
            <View style={styles.ctaIcon}>
              <ArrowRightMedium width={14} height={11} />
            </View>
          }
        />
      </View>
    );
  };

  return (
    <ScrollView
      style={styles.container}
      ref={scrollRef}
      contentContainerStyle={{ flexGrow: 1, paddingBottom: 40 }}
      ListHeaderComponent={ListHeaderComponent}
    >
      {messageData.map(renderListData)}
    </ScrollView>
  );
};

export default MessageBox;

const styles = StyleSheet.create({
  container: { borderWidth: 0, flex: 1 },
  messageContainer: {
    backgroundColor: "#ffffff",
    borderRadius: 5,
    padding: 10,
    marginHorizontal: 10,
    overflow: "hidden",
  },
  footerContainer: {
    marginTop: 52,
    paddingHorizontal: 16,
    marginBottom: 20,
  },
  ctaIcon: {
    marginLeft: 10,
  },
  buttonContainer: {
    marginTop: 8,
  },
});

const textFunc = (props) => {
  const str = props.children;
  return (
    <Text {...props}>
      {str?.split(" ").map((x) => {
        if (x.startsWith("**")) {
          x = x.replace(/\*/g, "");
          x += " ";
          return (
            <Text key={`text-${x}`} style={{ fontWeight: "bold" }}>
              {x}
            </Text>
          );
        } else {
          x += " ";
          return x;
        }
      })}
    </Text>
  );
};

export const CustomText = (props) => {
  return textFunc(props);
};
